import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  useCreateSectionParameterSchemaMutation,
  useDeleteSectionParameterSchemaMutation,
  useGetSectionParameterSchemasQuery,
  useUpdateSectionParameterSchemaMutation
} from 'data/generated/graphql';
import type { SectionParameterSchemaEntry } from './SectionParameterSettings';

export const useSectionParameterSchemas = (publicationKey: string) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useGetSectionParameterSchemasQuery(
    { publicationKey },
    { enabled: !!publicationKey }
  );
  const schemas: SectionParameterSchemaEntry[] = (data?.getSectionParameterSchemas ?? []).map((s) => ({
    key: s.key,
    label: s.label,
    type: s.type,
    enumValues: s.enumValues ?? undefined,
    deprecated: s.deprecated === true ? true : undefined,
    placeholder: s.placeholder ?? undefined,
    userExposed: s.userExposed === true ? true : undefined
  }));

  const invalidate = useCallback(() => {
    void queryClient.refetchQueries(['GetSectionParameterSchemas']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, publicationKey]);

  const createMutation = useCreateSectionParameterSchemaMutation();
  const updateMutation = useUpdateSectionParameterSchemaMutation();
  const deleteMutation = useDeleteSectionParameterSchemaMutation();

  const createSchema = useCallback(
    (entry: SectionParameterSchemaEntry) =>
      new Promise<void>((resolve, reject) => {
        createMutation.mutate(
          {
            publicationKey,
            input: {
              key: entry.key,
              label: entry.label,
              type: entry.type,
              enumValues: entry.enumValues ?? null,
              deprecated: entry.deprecated ?? null,
              placeholder: entry.placeholder ?? null,
              userExposed: entry.userExposed ?? null
            }
          },
          {
            onSuccess: () => {
              invalidate();
              resolve();
            },
            onError: reject
          }
        );
      }),
    [createMutation, publicationKey, invalidate]
  );

  const updateSchema = useCallback(
    (key: string, entry: SectionParameterSchemaEntry) =>
      new Promise<void>((resolve, reject) => {
        updateMutation.mutate(
          {
            publicationKey,
            key,
            input: {
              key: entry.key,
              label: entry.label,
              type: entry.type,
              enumValues: entry.enumValues ?? null,
              deprecated: entry.deprecated ?? null,
              placeholder: entry.placeholder ?? null,
              userExposed: entry.userExposed ?? null
            }
          },
          {
            onSuccess: () => {
              invalidate();
              resolve();
            },
            onError: reject
          }
        );
      }),
    [updateMutation, publicationKey, invalidate]
  );

  const deleteSchema = useCallback(
    (key: string) =>
      new Promise<void>((resolve, reject) => {
        deleteMutation.mutate(
          { publicationKey, key },
          {
            onSuccess: () => {
              invalidate();
              resolve();
            },
            onError: reject
          }
        );
      }),
    [deleteMutation, publicationKey, invalidate]
  );

  return {
    schemas,
    isLoading,
    error,
    createSchema,
    updateSchema,
    deleteSchema
  };
};
